import { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { normalizarTipo } from './pedidoDocumento';
import type { Camion, Cliente, Cola, Conductor, Contrato, Envasadora, Pedido, RefCamion, RefCola, Ruta, Venta } from '../types';

// ── PERSISTENCIA REAL EN SUPABASE ────────────────────────────────────────────
// A diferencia del resto de la app (que guarda todo en el blob genérico
// `app_state`), el módulo de Gas Industrial lee y escribe directo en sus
// propias tablas relacionales: gas_clientes, gas_contratos, gas_rutas y
// gas_ventas (ver sql/instalar_todo.sql). Mientras esas tablas no existan en el
// proyecto de Supabase, se usa un respaldo en localStorage para que la app
// no se rompa, y se avisa por consola (y en `synced`) que faltan crear.

type Estado = 'synced' | 'local-only' | 'loading';
function cacheGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}
function cacheSet<T>(key: string, value: T) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {

    /* ignore */}
}
function esTablaFaltante(error: {
  code?: string;
  message?: string;
} | null) {
  if (!error) return false;
  const msg = error.message || '';
  return error.code === '42P01' || error.code === 'PGRST205' || /could not find the table|does not exist/i.test(msg);
}

// ── Mapeo camelCase (app) <-> snake_case (columnas de Supabase) ─────────────

function clienteToRow(c: Cliente) {
  return {
    id: c.id,
    codigo: c.codigo,
    nombre: c.nombre,
    rnc: c.rnc,
    contacto: c.contacto,
    telefono: c.telefono,
    email: c.email,
    direccion: c.direccion,
    ubicacion: c.ubicacion,
    tipo: c.tipo,
    capacidad_tanque: c.capacidadTanque,
    estado: c.estado,
    observaciones: c.observaciones,
    fecha_registro: c.fecha_registro || null,
    ultima_compra: c.ultima_compra || null,
    dias_sin_comprar: c.dias_sin_comprar ?? null,
    representante: c.representante || '',
    representante_cedula: c.representanteCedula || '',
    representante_direccion: c.representanteDireccion || ''
  };
}
function rowToCliente(r: any): Cliente {
  return {
    id: r.id,
    codigo: r.codigo || '',
    nombre: r.nombre || '',
    rnc: r.rnc || '',
    contacto: r.contacto || '',
    telefono: r.telefono || '',
    email: r.email || '',
    direccion: r.direccion || '',
    ubicacion: r.ubicacion || '',
    tipo: r.tipo || 'Comercio',
    capacidadTanque: r.capacidad_tanque || 0,
    estado: r.estado || 'activo',
    observaciones: r.observaciones || '',
    fecha_registro: r.fecha_registro || '',
    ultima_compra: r.ultima_compra || undefined,
    dias_sin_comprar: r.dias_sin_comprar ?? undefined,
    representante: r.representante || '',
    representanteCedula: r.representante_cedula || '',
    representanteDireccion: r.representante_direccion || ''
  };
}
function contratoToRow(c: Contrato) {
  return {
    id: c.id,
    numero_contrato: c.numeroContrato,
    cliente_id: c.clienteId,
    cliente_nombre: c.clienteNombre,
    fecha_inicio: c.fechaInicio || null,
    fecha_vencimiento: c.fechaVencimiento || null,
    precio_acordado: c.precioAcordado,
    condiciones: c.condiciones,
    estado: c.estado,
    firma_digital: c.firmaDigital || null,
    fecha_creacion: c.fecha_creacion || null,
    cantidad_tanques: c.cantidadTanques ?? 1,
    capacidad_galones: c.capacidadGalones ?? null,
    valor_tanque: c.valorTanque ?? null,
    ciudad: c.ciudad || 'Santiago de los Caballeros'
  };
}
function rowToContrato(r: any): Contrato {
  return {
    id: r.id,
    numeroContrato: r.numero_contrato || '',
    clienteId: r.cliente_id || '',
    clienteNombre: r.cliente_nombre || '',
    fechaInicio: r.fecha_inicio || '',
    fechaVencimiento: r.fecha_vencimiento || '',
    precioAcordado: r.precio_acordado || 0,
    condiciones: r.condiciones || '',
    estado: r.estado || 'vigente',
    firmaDigital: r.firma_digital || undefined,
    fecha_creacion: r.fecha_creacion || '',
    cantidadTanques: r.cantidad_tanques ?? 1,
    capacidadGalones: r.capacidad_galones ?? undefined,
    valorTanque: r.valor_tanque ?? undefined,
    ciudad: r.ciudad || 'Santiago de los Caballeros'
  };
}
function rutaToRow(r: Ruta) {
  return {
    id: r.id,
    nombre: r.nombre,
    zona: r.zona,
    supervisor: r.supervisor,
    clientes: r.clientes || [],
    fecha_creacion: r.fechaCreacion || null,
    dias_semana: r.diasSemana || [],
    estado: r.estado
  };
}
function rowToRuta(r: any): Ruta {
  return {
    id: r.id,
    nombre: r.nombre || '',
    zona: r.zona || '',
    supervisor: r.supervisor || '',
    clientes: r.clientes || [],
    fechaCreacion: r.fecha_creacion || '',
    diasSemana: r.dias_semana || [],
    estado: r.estado || 'activa'
  };
}
function ventaToRow(v: Venta) {
  return {
    id: v.id,
    numero_factura: v.numeroFactura,
    cliente_id: v.clienteId,
    cliente_nombre: v.clienteNombre,
    fecha: v.fecha || null,
    cantidad: v.cantidad,
    precio: v.precio,
    total: v.total,
    ruta: v.ruta,
    supervisor: v.supervisor,
    observaciones: v.observaciones
  };
}
function rowToVenta(r: any): Venta {
  return {
    id: r.id,
    numeroFactura: r.numero_factura || '',
    clienteId: r.cliente_id || '',
    clienteNombre: r.cliente_nombre || '',
    fecha: r.fecha || '',
    cantidad: r.cantidad || 0,
    precio: r.precio || 0,
    total: r.total || 0,
    ruta: r.ruta || '',
    supervisor: r.supervisor || '',
    observaciones: r.observaciones || ''
  };
}
function pedidoToRow(p: Pedido) {
  return {
    id: p.id,
    tipo: p.tipo,
    numero_orden: p.numeroOrden,
    fecha: p.fecha || null,
    conductor: p.conductor,
    pase_chofer: p.paseChofer,
    ficha_cola: p.fichaCola,
    placa_camion: p.placaCamion,
    // El detalle es de largo variable (1 línea en GLP, hasta 6 en WOP) y solo
    // se consulta junto al pedido, así que va como jsonb en vez de una tabla
    // hija: evita un join para algo que nunca se filtra por línea.
    lineas: p.lineas,
    estado: p.estado,
    autorizado_por: p.autorizadoPor,
    autorizado_cargo: p.autorizadoCargo,
    autorizado_fecha: p.autorizadoFecha || null,
    token: p.token || null,
    observaciones: p.observaciones,
    fecha_creacion: p.fecha_creacion || null
  };
}
function rowToPedido(r: any): Pedido {
  return {
    id: r.id,
    // Las filas guardadas antes de separar los dos suplidores traen 'GLP' a
    // secas; normalizarTipo las manda a Refinería, que es a donde iban.
    tipo: normalizarTipo(r.tipo || ''),
    numeroOrden: r.numero_orden || '',
    fecha: r.fecha || '',
    conductor: r.conductor || '',
    paseChofer: r.pase_chofer || '',
    fichaCola: r.ficha_cola || '',
    placaCamion: r.placa_camion || '',
    lineas: Array.isArray(r.lineas) ? r.lineas : [],
    estado: r.estado || 'borrador',
    autorizadoPor: r.autorizado_por || '',
    autorizadoCargo: r.autorizado_cargo || '',
    autorizadoFecha: r.autorizado_fecha || '',
    token: r.token || '',
    observaciones: r.observaciones || '',
    fecha_creacion: r.fecha_creacion || ''
  };
}
function camionToRow(c: Camion) {
  return {
    id: c.id,
    nombre: c.nombre,
    placa: c.placa,
    plantas_vetadas: c.plantasVetadas ?? [],
    solo_conductor_id: c.soloConductorId || '',
    solo_conductor_nombre: c.soloConductorNombre || '',
    activo: c.activo,
    fecha_registro: c.fecha_registro || null
  };
}
function rowToCamion(r: any): Camion {
  return {
    id: r.id,
    // Antes el nombre del camión se guardaba como "descripción"; las filas
    // viejas se leen desde ahí para no perderlo.
    nombre: r.nombre || r.descripcion || '',
    placa: r.placa || '',
    plantasVetadas: Array.isArray(r.plantas_vetadas) ? r.plantas_vetadas : [],
    soloConductorId: r.solo_conductor_id || '',
    soloConductorNombre: r.solo_conductor_nombre || '',
    // Las filas viejas sin la columna se consideran activas.
    activo: r.activo !== false,
    fecha_registro: r.fecha_registro || ''
  };
}
function colaToRow(c: Cola) {
  return {
    id: c.id,
    ficha: c.ficha,
    capacidad_galones: c.capacidadGalones || 0,
    descripcion: c.descripcion,
    activo: c.activo,
    fecha_registro: c.fecha_registro || null
  };
}
function rowToCola(r: any): Cola {
  return {
    id: r.id,
    ficha: r.ficha || '',
    capacidadGalones: Number(r.capacidad_galones) || 0,
    descripcion: r.descripcion || '',
    activo: r.activo !== false,
    fecha_registro: r.fecha_registro || ''
  };
}
function envasadoraToRow(e: Envasadora) {
  return {
    id: e.id,
    nombre: e.nombre,
    ciudad: e.ciudad || '',
    rnc: e.rnc,
    direccion: e.direccion,
    telefono: e.telefono,
    colas_permitidas: e.colasPermitidas ?? [],
    colas_segunda_opcion: e.colasSegundaOpcion ?? [],
    activo: e.activo,
    fecha_registro: e.fecha_registro || null
  };
}
function rowToEnvasadora(r: any): Envasadora {
  return {
    id: r.id,
    nombre: r.nombre || '',
    ciudad: r.ciudad || '',
    rnc: r.rnc || '',
    direccion: r.direccion || '',
    telefono: r.telefono || '',
    // Lista vacía = admite todas las colas.
    colasPermitidas: Array.isArray(r.colas_permitidas) ? r.colas_permitidas : [],
    colasSegundaOpcion: Array.isArray(r.colas_segunda_opcion) ? r.colas_segunda_opcion : [],
    activo: r.activo !== false,
    fecha_registro: r.fecha_registro || ''
  };
}
function conductorToRow(c: Conductor) {
  return {
    id: c.id,
    nombre: c.nombre,
    pase: c.pase,
    telefono: c.telefono,
    // Listas de largo variable que solo se consultan junto al conductor: van
    // como jsonb, igual que `lineas` en gas_pedidos, en vez de tablas hijas.
    camiones: c.camiones ?? [],
    colas: c.colas ?? [],
    activo: c.activo,
    fecha_registro: c.fecha_registro || null
  };
}
function rowToConductor(r: any): Conductor {
  // Compatibilidad con las filas guardadas cuando un conductor solo podía
  // tener un camión y una cola: se leen como listas de uno.
  const camiones: RefCamion[] = Array.isArray(r.camiones) ? r.camiones : r.placa ? [{
    id: r.camion_id || '',
    placa: r.placa
  }] : [];
  const colas: RefCola[] = Array.isArray(r.colas) ? r.colas : r.ficha_cola ? [{
    id: r.cola_id || '',
    ficha: r.ficha_cola
  }] : [];
  return {
    id: r.id,
    nombre: r.nombre || '',
    pase: r.pase || '',
    telefono: r.telefono || '',
    camiones,
    colas,
    // Las filas viejas sin la columna se consideran activas.
    activo: r.activo !== false,
    fecha_registro: r.fecha_registro || ''
  };
}

// Envuelve una escritura para que un fallo de red se vea como un `error`
// normal y no como una promesa rechazada: quien llama no siempre hace await,
// y una rejection suelta se traga el fallo sin que nadie se entere.
async function escribir(fn: () => PromiseLike<{
  error: {
    code?: string;
    message?: string;
  } | null;
}>): Promise<{
  error: {
    code?: string;
    message?: string;
  } | null;
}> {
  try {
    return await fn();
  } catch (e: any) {
    return {
      error: {
        message: e?.message || 'No se pudo conectar con el servidor.'
      }
    };
  }
}

// ── Hook genérico: tabla real de Supabase + caché local + estado de sync ───

function useGasTable<T extends {
  id: string;
}>(table: string, cacheKey: string, toRow: (x: T) => any, fromRow: (r: any) => T) {
  const [items, setItems] = useState<T[]>(() => cacheGet<T[]>(cacheKey, []));
  const [estado, setEstado] = useState<Estado>('loading');
  // Ids que se guardaron en el navegador pero NO llegaron a Supabase: o la
  // tabla todavía no existía, o la escritura falló. Antes se perdían para
  // siempre; ahora quedan anotados y se reintentan en la siguiente carga.
  // Sin esto, crear registros con la tabla sin crear y correr el SQL después
  // dejaba las tablas vacías: es exactamente el "creé datos y no se reflejan".
  const pendKey = `${cacheKey}.pendientes`;
  const [pendientes, setPendientes] = useState<string[]>(() => cacheGet<string[]>(pendKey, []));
  const tablaFaltante = useRef(false);
  // Copia viva de los items para no leer estado rancio dentro de los callbacks:
  // dos altas seguidas leían el mismo `items` de un render viejo y la segunda
  // borraba la primera del caché local.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const anotarPendiente = (id: string, si: boolean) => {
    setPendientes(prev => {
      const next = si ? Array.from(new Set([...prev, id])) : prev.filter(x => x !== id);
      cacheSet(pendKey, next);
      return next;
    });
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {
          data,
          error
        } = await supabase.from(table).select('*');
        if (cancelled) return;
        if (error) {
          if (esTablaFaltante(error)) {
            tablaFaltante.current = true;
            console.warn(`[gas] La tabla "${table}" no existe en Supabase todavía. ` + `Corre sql/instalar_todo.sql en el SQL Editor para activar la sincronización real. ` + `Por ahora se usa solo almacenamiento local.`);
          } else {
            console.warn(`[gas] No se pudo leer "${table}":`, error.message);
          }
          setEstado('local-only');
          return;
        }
        const rows = (data || []).map(fromRow);
        const enServidor = new Set(rows.map(r => r.id));

        // Lo que quedó pendiente y sigue en el navegador se sube ahora. Se usa
        // upsert para que dé igual si la fila ya existía a medias.
        const porSubir = cacheGet<string[]>(pendKey, []).map(id => itemsRef.current.find(x => x.id === id)).filter(Boolean) as T[];
        let subidos = 0;
        for (const item of porSubir) {
          const {
            error: err
          } = await escribir(() => supabase.from(table).upsert(toRow(item)));
          if (cancelled) return;
          if (err) {
            console.warn(`[gas] Sigue sin poder subirse a "${table}" el registro ${item.id}:`, err.message);
            continue;
          }
          subidos++;
          anotarPendiente(item.id, false);
          if (!enServidor.has(item.id)) rows.push(item);
        }
        if (subidos) {
          console.info(`[gas] Se subieron ${subidos} registro(s) que estaban pendientes en "${table}".`);
        }
        setItems(rows);
        cacheSet(cacheKey, rows);
        setEstado('synced');
      } catch (e) {
        if (!cancelled) setEstado('local-only');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);
  const persist = (next: T[]) => {
    itemsRef.current = next;
    setItems(next);
    cacheSet(cacheKey, next);
  };
  const add = async (item: T) => {
    persist([...itemsRef.current, item]);
    if (tablaFaltante.current) {
      anotarPendiente(item.id, true);
      return;
    }
    // try/catch además del `error`: si se cae la red, supabase-js puede
    // rechazar la promesa en vez de devolver un error, y sin esto el registro
    // se perdía con un "unhandled rejection" que nadie ve.
    const {
      error
    } = await escribir(() => supabase.from(table).upsert(toRow(item)));
    if (error) {
      anotarPendiente(item.id, true);
      if (esTablaFaltante(error)) {
        tablaFaltante.current = true;
        setEstado('local-only');
      } else {
        console.warn(`[gas] No se pudo guardar en "${table}":`, error.message);
        alert(`No se pudo guardar en Supabase (${table}): ${error.message}\n` + `El registro quedó guardado localmente en este navegador y se reintentará al recargar.`);
      }
    } else {
      anotarPendiente(item.id, false);
      setEstado('synced');
    }
  };
  const update = async (item: T) => {
    persist(itemsRef.current.map(x => x.id === item.id ? item : x));
    if (tablaFaltante.current) {
      anotarPendiente(item.id, true);
      return;
    }
    // upsert y no update: si la fila nunca llegó a crearse arriba (quedó
    // pendiente), un update no afecta ninguna fila y el cambio se perdería
    // sin dar ni un error.
    const {
      error
    } = await escribir(() => supabase.from(table).upsert(toRow(item)));
    if (error) {
      anotarPendiente(item.id, true);
      if (!esTablaFaltante(error)) {
        console.warn(`[gas] No se pudo actualizar en "${table}":`, error.message);
        alert(`No se pudo actualizar en Supabase (${table}): ${error.message}\n` + `El cambio quedó en este navegador y se reintentará al recargar.`);
      }
    } else {
      anotarPendiente(item.id, false);
      setEstado('synced');
    }
  };
  const remove = async (id: string) => {
    persist(itemsRef.current.filter(x => x.id !== id));
    anotarPendiente(id, false);
    if (tablaFaltante.current) return;
    const {
      error
    } = await escribir(() => supabase.from(table).delete().eq('id', id));
    if (error && !esTablaFaltante(error)) {
      console.warn(`[gas] No se pudo eliminar en "${table}":`, error.message);
      alert(`No se pudo eliminar en Supabase (${table}): ${error.message}`);
    }
  };
  return {
    items,
    setItemsLocal: persist,
    estado,
    // Cuántos registros siguen sin subir. La UI lo enseña para que nadie crea
    // que guardó algo que solo existe en su navegador.
    pendientes: pendientes.length,
    add,
    update,
    remove
  };
}
export function useGasData() {
  const clientesQ = useGasTable<Cliente>('gas_clientes', 'gas.clientes', clienteToRow, rowToCliente);
  const contratosQ = useGasTable<Contrato>('gas_contratos', 'gas.contratos', contratoToRow, rowToContrato);
  const rutasQ = useGasTable<Ruta>('gas_rutas', 'gas.rutas', rutaToRow, rowToRuta);
  const ventasQ = useGasTable<Venta>('gas_ventas', 'gas.ventas', ventaToRow, rowToVenta);

  // Al registrar una venta se actualiza "última compra" del cliente
  // correspondiente, tanto local como en la tabla gas_clientes.
  const onAddVenta = async (v: Venta) => {
    await ventasQ.add(v);
    const cliente = clientesQ.items.find(c => c.id === v.clienteId);
    if (cliente) {
      await clientesQ.update({
        ...cliente,
        ultima_compra: v.fecha
      });
    }
  };

  // Estado combinado: si cualquiera de las 4 tablas no existe todavía,
  // lo mostramos en la UI para que el usuario sepa que debe correr el SQL.
  const estados = [clientesQ.estado, contratosQ.estado, rutasQ.estado, ventasQ.estado];
  const sinTablas = estados.some(e => e === 'local-only');
  const cargando = estados.every(e => e === 'loading');
  return {
    clientes: clientesQ.items,
    contratos: contratosQ.items,
    rutas: rutasQ.items,
    ventas: ventasQ.items,
    sinTablas,
    cargando,
    onAddCliente: clientesQ.add,
    onUpdateCliente: clientesQ.update,
    onDeleteCliente: clientesQ.remove,
    onAddContrato: contratosQ.add,
    onUpdateContrato: contratosQ.update,
    onDeleteContrato: contratosQ.remove,
    onAddRuta: rutasQ.add,
    onUpdateRuta: rutasQ.update,
    onDeleteRuta: rutasQ.remove,
    onAddVenta,
    onUpdateVenta: ventasQ.update,
    onDeleteVenta: ventasQ.remove
  };
}

// Lee un pedido por su token público. Es la consulta que hace la página de
// verificación cuando alguien escanea el QR del formulario impreso: no hay
// sesión ni caché local, solo el token que venía en la URL.
export async function fetchPedidoPorToken(token: string): Promise<{
  pedido: Pedido | null;
  error: string | null;
}> {
  if (!token) return {
    pedido: null,
    error: 'Falta el código de verificación.'
  };
  try {
    const {
      data,
      error
    } = await supabase.from('gas_pedidos').select('*').eq('token', token).maybeSingle();
    if (error) {
      if (esTablaFaltante(error)) {
        return {
          pedido: null,
          error: 'La tabla de pedidos todavía no existe en Supabase. Corre sql/instalar_todo.sql.'
        };
      }
      return {
        pedido: null,
        error: 'No se pudo consultar el documento.'
      };
    }
    if (!data) return {
      pedido: null,
      error: null
    };
    return {
      pedido: rowToPedido(data),
      error: null
    };
  } catch {
    return {
      pedido: null,
      error: 'No se pudo conectar con el servidor.'
    };
  }
}

// Las órdenes de pedido viven en su propia tabla y se montan como módulo
// aparte (no dentro de Gas Industrial), por eso tienen su propio hook en vez
// de colgar de `useGasData`. Reusa la misma mecánica: Supabase si la tabla
// existe, localStorage como respaldo.
export function usePedidos() {
  const pedidosQ = useGasTable<Pedido>('gas_pedidos', 'gas.pedidos', pedidoToRow, rowToPedido);
  return {
    pedidos: pedidosQ.items,
    sinTabla: pedidosQ.estado === 'local-only',
    cargando: pedidosQ.estado === 'loading',
    onAddPedido: pedidosQ.add,
    onUpdatePedido: pedidosQ.update,
    onDeletePedido: pedidosQ.remove
  };
}

// Flota y destinos: los cuatro catálogos que alimentan el formulario de orden
// de pedido. Viven aparte de `useGasData` por lo mismo que `usePedidos`: los
// consume el módulo de Órdenes, no Gas Industrial.
export function useFlota() {
  const camionesQ = useGasTable<Camion>('gas_camiones', 'gas.camiones', camionToRow, rowToCamion);
  const colasQ = useGasTable<Cola>('gas_colas', 'gas.colas', colaToRow, rowToCola);
  const envasadorasQ = useGasTable<Envasadora>('gas_envasadoras', 'gas.envasadoras', envasadoraToRow, rowToEnvasadora);
  const conductoresQ = useGasTable<Conductor>('gas_conductores', 'gas.conductores', conductorToRow, rowToConductor);

  // Archivar un camión o una cola lo saca de servicio: deja de ofrecerse al
  // asignar y se quita de los conductores que lo tenían. No se borra nada, así
  // que las órdenes ya firmadas con esa unidad siguen intactas.
  const onArchivarCamion = async (c: Camion, archivar: boolean) => {
    await camionesQ.update({
      ...c,
      activo: !archivar
    });
    if (!archivar) return;
    for (const k of conductoresQ.items.filter(x => (x.camiones ?? []).some(u => u.id === c.id))) {
      await conductoresQ.update({
        ...k,
        camiones: (k.camiones ?? []).filter(u => u.id !== c.id)
      });
    }
  };
  const onArchivarCola = async (c: Cola, archivar: boolean) => {
    await colasQ.update({
      ...c,
      activo: !archivar
    });
    if (!archivar) return;
    for (const k of conductoresQ.items.filter(x => (x.colas ?? []).some(u => u.id === c.id))) {
      await conductoresQ.update({
        ...k,
        colas: (k.colas ?? []).filter(u => u.id !== c.id)
      });
    }
  };
  const onArchivarEnvasadora = async (e: Envasadora, archivar: boolean) => envasadorasQ.update({
    ...e,
    activo: !archivar
  });
  const onArchivarConductor = async (c: Conductor, archivar: boolean) => conductoresQ.update({
    ...c,
    activo: !archivar
  });
  const estados = [camionesQ.estado, colasQ.estado, envasadorasQ.estado, conductoresQ.estado];
  return {
    camiones: camionesQ.items,
    colas: colasQ.items,
    envasadoras: envasadorasQ.items,
    conductores: conductoresQ.items,
    sinTablas: estados.some(e => e === 'local-only'),
    pendientes: camionesQ.pendientes + colasQ.pendientes + envasadorasQ.pendientes + conductoresQ.pendientes,
    onAddCamion: camionesQ.add,
    onUpdateCamion: camionesQ.update,
    onArchivarCamion,
    onAddCola: colasQ.add,
    onUpdateCola: colasQ.update,
    onArchivarCola,
    onAddEnvasadora: envasadorasQ.add,
    onUpdateEnvasadora: envasadorasQ.update,
    onArchivarEnvasadora,
    onArchivarConductor,
    onAddConductor: conductoresQ.add,
    onUpdateConductor: conductoresQ.update
  };
}